/**
 * PublicFeedbackPage — customer post-trip feedback (/f/:token).
 * No auth, no external dependencies (inline SVG stars, existing styles only) —
 * consistent with the /t/ and /d/ public pages. Shows only trip number + date;
 * never any other PII. One submission per booking (server-enforced); friendly
 * states for expired / revoked / not-yet-active / already-answered.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, XCircle, CalendarClock, CheckCircle2, Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { usePublicLanguage } from '@/components/public/usePublicLanguage';
import PublicLanguageToggle from '@/components/public/PublicLanguageToggle';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (d?: string | null, lang = 'no') => {
  if (!d) return null;
  try { return new Date(d + 'T00:00:00').toLocaleDateString(lang === 'en' ? 'en-GB' : 'nb-NO', { day: 'numeric', month: 'long', year: 'numeric' }); }
  catch { return d; }
};

function Shell({ children, testid, toggle }: { children: React.ReactNode; testid: string; toggle?: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center relative" data-testid={testid}>
        {toggle && <div className="absolute right-3 top-3">{toggle}</div>}
        {children}
      </div>
    </div>
  );
}

export default function PublicFeedbackPage() {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<any>(null);
  const { lang, setPublicLanguage } = usePublicLanguage(data?.preferred_language);
  const toggle = <PublicLanguageToggle lang={lang} onChange={setPublicLanguage} />;
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');
  const [contact, setContact] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/public/feedback/${token}`);
      if (res.status === 404) { setNotFound(true); return; }
      if (!res.ok) return;
      setData(await res.json());
    } catch { /* keep */ }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (rating < 1) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/public/feedback/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating, comment: comment.slice(0, 2000), contact_requested: contact }),
      });
      const j = await res.json();
      if (res.ok && j.success) setSubmitted(true);
    } catch { /* ignore */ }
    finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" data-testid="feedback-loading" />
      </div>
    );
  }

  if (notFound) {
    return (
      <Shell testid="feedback-not-found" toggle={toggle}>
        <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h1 className="text-xl font-bold text-slate-800 mb-2">{t('publicFeedbackPage.lenkenErIkkeGyldig', 'Lenken er ikke gyldig')}</h1>
        <p className="text-sm text-slate-500">{t('publicFeedbackPage.lenkenKanVaereFeilEller', 'Lenken kan være feil eller utløpt. Kontakt oss hvis du mener dette er en feil.')}</p>
      </Shell>
    );
  }

  const company = data?.company || {};
  const brand = (
    <div className="text-center mb-4 relative">
      <div className="absolute right-0 top-0">{toggle}</div>
      {company.logo && <img src={company.logo} alt={company.name || ''} className="h-10 mx-auto mb-2 object-contain" />}
      <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider" data-testid="feedback-company">{company.name}</p>
    </div>
  );

  if (data?.state === 'expired') {
    return (
      <Shell testid="feedback-expired" toggle={toggle}>
        <CalendarClock className="w-12 h-12 text-slate-400 mx-auto mb-4" />
        <h1 className="text-xl font-bold text-slate-800 mb-2">{t('publicFeedbackPage.lenkenErUtlopt', 'Lenken er utløpt')}</h1>
        <p className="text-sm text-slate-500">{t('publicFeedbackPage.tilbakemeldingslenkenHarGattUtPa', 'Tilbakemeldingslenken har gått ut på tid.')}</p>
      </Shell>
    );
  }
  if (data?.state === 'revoked') {
    return (
      <Shell testid="feedback-revoked" toggle={toggle}>
        <XCircle className="w-12 h-12 text-slate-400 mx-auto mb-4" />
        <h1 className="text-xl font-bold text-slate-800 mb-2">{t('publicFeedbackPage.lenkenErDeaktivert', 'Lenken er deaktivert')}</h1>
        <p className="text-sm text-slate-500">{t('publicFeedbackPage.kontaktOssHvisDuFortsatt', 'Kontakt oss hvis du fortsatt ønsker å gi tilbakemelding.')}</p>
      </Shell>
    );
  }
  if (data?.state === 'not_yet_active') {
    return (
      <Shell testid="feedback-not-yet-active" toggle={toggle}>
        <CalendarClock className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
        <h1 className="text-xl font-bold text-slate-800 mb-2">{t('publicFeedbackPage.ikkeTilgjengeligEnna', 'Ikke tilgjengelig ennå')}</h1>
        <p className="text-sm text-slate-500">{t('publicFeedbackPage.tilbakemeldingApnerEtterAtTuren', 'Tilbakemelding åpner etter at turen er fullført.')}</p>
      </Shell>
    );
  }

  if (submitted || data?.state === 'already') {
    return (
      <Shell testid="feedback-thanks">
        {brand}
        <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-4" />
        <h1 className="text-xl font-bold text-slate-800 mb-2">{t('publicFeedbackPage.takkForTilbakemeldingen', 'Takk for tilbakemeldingen!')}</h1>
        <p className="text-sm text-slate-500">
          {data?.state === 'already' && !submitted
            ? t('publicFeedbackPage.alleredeVurdert', 'Denne turen er allerede vurdert. Takk!')
            : t('publicFeedbackPage.dinVurderingHjelperOss', 'Din vurdering hjelper oss å bli bedre.')}
        </p>
      </Shell>
    );
  }

  // state === 'active' → the form
  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4" data-testid="feedback-page">
      <div className="max-w-md mx-auto">
        {brand}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h1 className="text-2xl font-bold text-slate-800 text-center">{t('publicFeedbackPage.hvordanVarTuren', 'Hvordan var turen?')}</h1>
          <p className="text-sm text-slate-500 text-center mt-1" data-testid="feedback-trip">
            {t('public.trip', 'Tur')} {data?.trip_number || '—'}{data?.date ? ` · ${fmtDate(data.date, lang)}` : ''}
          </p>

          {/* Star rating */}
          <div className="flex items-center justify-center gap-2 my-6" data-testid="feedback-stars">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setRating(n)}
                onMouseEnter={() => setHover(n)}
                onMouseLeave={() => setHover(0)}
                data-testid={`feedback-star-${n}`}
                className="p-1 transition-transform hover:scale-110"
                aria-label={t('publicFeedbackPage.nStjerner', '{{count}} stjerner', { count: n })}
              >
                <Star
                  className={`w-9 h-9 ${(hover || rating) >= n ? 'text-amber-400 fill-amber-400' : 'text-slate-300'}`}
                />
              </button>
            ))}
          </div>

          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value.slice(0, 2000))}
            maxLength={2000}
            placeholder={t('publicFeedbackPage.vilDuFortelleOssMer', 'Vil du fortelle oss mer? (valgfritt)')}
            rows={4}
            data-testid="feedback-comment"
            className="w-full rounded-lg border border-slate-200 p-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-400 resize-none"
          />
          <div className="text-right text-[11px] text-slate-400">{comment.length}/2000</div>

          <label className="flex items-center gap-2 mt-2 text-sm text-slate-600 cursor-pointer" data-testid="feedback-contact-label">
            <input
              type="checkbox"
              checked={contact}
              onChange={(e) => setContact(e.target.checked)}
              data-testid="feedback-contact"
              className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-400"
            />
            {t('publicFeedbackPage.jegOnskerABliKontaktet', 'Jeg ønsker å bli kontaktet')}
          </label>

          <button
            type="button"
            onClick={submit}
            disabled={rating < 1 || submitting}
            data-testid="feedback-submit"
            className="w-full mt-5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-2"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {t('publicFeedbackPage.sendTilbakemelding', 'Send tilbakemelding')}
          </button>
        </div>
      </div>
    </div>
  );
}
